import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages, ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const L10N = [
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "ar", label: "العربية" },
];

const normLng = (lng) => (lng ? String(lng).slice(0, 2).toLowerCase() : "fr");
const byCode = (code) => L10N.find((x) => x.code === normLng(code));

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const itemsRef = useRef({}); // refs je Option

  const cur = normLng(i18n.language);
  const curLabel = byCode(cur)?.label || cur.toUpperCase();
  const dir = i18n.dir ? i18n.dir(cur) : cur === "ar" ? "rtl" : "ltr";
  const menuPos = dir === "rtl" ? "left-0" : "right-0";
  const ringDir = "focus:outline-none focus:ring-2 focus:ring-warm-sage/50";

  const applyHtmlAttrs = (lng) => {
    const L = normLng(lng);
    document.documentElement.lang = L;
    document.documentElement.dir = L === "ar" ? "rtl" : "ltr";
    document.body.style.fontFamily = L === "ar" ? "'Cairo', sans-serif" : "";
  };

  const setLang = (newLang) => {
    const L = normLng(newLang);
    if (!byCode(L)) return;

    // 1) i18n & Persist
    i18n.changeLanguage(L);
    localStorage.setItem("lang", L);
    localStorage.setItem("i18nextLng", L);
    applyHtmlAttrs(L);

    // 2) URL sync (Hash beibehalten)
    const params = new URLSearchParams(location.search);
    params.set("lang", L);
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
        hash: location.hash,
      },
      { replace: false }
    );

    setOpen(false);

    // 3) Optional: sanft zum Hero scrollen, falls vorhanden
    requestAnimationFrame(() => {
      document.getElementById("accueil")?.scrollIntoView({ behavior: "smooth" });
    });
  };

  // Click-Outside zum Schließen
  useEffect(() => {
    const onDocPointer = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, []);

  // Bei Routen- oder Sprachwechsel: Menü schließen
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, i18n.language]);

  // Keyboard-Steuerung fürs Menü
  const onButtonKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      // Fokus auf aktuelle Sprache setzen
      const curRef = itemsRef.current[cur];
      requestAnimationFrame(() => curRef?.focus());
    }
  };

  const onItemKeyDown = (e, code) => {
    const idx = L10N.findIndex((x) => x.code === code);
    if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = L10N[(idx + 1) % L10N.length].code;
      itemsRef.current[next]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = L10N[(idx - 1 + L10N.length) % L10N.length].code;
      itemsRef.current[prev]?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setLang(code);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Sprache wählen (aktuell: ${curLabel})`}
        className={`group flex items-center gap-2 px-4 py-2 rounded-lg bg-warm-sage/10 hover:bg-warm-sage/20 border border-warm-sage/30 hover:border-warm-sage/50 transition-all duration-300 ${ringDir}`}
      >
        <Languages className="h-5 w-5 text-warm-sage group-hover:rotate-180 transition-transform duration-500" />
        <span className="text-sm font-medium text-charcoal">{curLabel}</span>
        <ChevronDown className="h-4 w-4 text-warm-sage/80" />
      </button>

      {/* Menu */}
      {open && (
        <div
          role="menu"
          aria-label="Sprachauswahl"
          dir={dir}
          className={`absolute ${menuPos} z-50 mt-2 min-w-[180px] rounded-xl border border-warm-sage/30 bg-cream-white shadow-lg`}
        >
          <ul className="py-1">
            {L10N.map(({ code, label }) => {
              const active = code === cur;
              return (
                <li key={code}>
                  <button
                    ref={(el) => (itemsRef.current[code] = el)}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    onClick={() => setLang(code)}
                    onKeyDown={(e) => onItemKeyDown(e, code)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm 
                      ${active ? "bg-warm-sage/15 text-charcoal font-medium" : "text-charcoal/90 hover:bg-warm-sage/10"}
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-sage/40 rounded-lg`}
                  >
                    <span>{label}</span>
                    {active && (
                      <span className="text-[10px] uppercase tracking-wider text-warm-sage">
                        aktiv
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
